/**
 * Speech Input
 *
 * Voice input button with Web Speech and MediaRecorder fallback support.
 *
 * Primary responsibilities:
 * - Provide a reusable voice capture control for chat surfaces
 * - Support controlled recorder flows without taking over their lifecycle
 * - Convert fallback recordings through a caller-provided transcription service
 *
 * Dependencies:
 * - components/ui/button
 * - lucide-react
 */

'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps
} from 'react'
import { Loader2, Mic } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type SpeechInputMode = 'speech-recognition' | 'media-recorder' | 'unsupported'

type SpeechRecognitionAlternativeLike = {
  transcript: string
}

type SpeechRecognitionResultLike = {
  isFinal: boolean
  [index: number]: SpeechRecognitionAlternativeLike
}

type SpeechRecognitionResultListLike = {
  length: number
  [index: number]: SpeechRecognitionResultLike
}

type SpeechRecognitionEventLike = Event & {
  resultIndex: number
  results: SpeechRecognitionResultListLike
}

type SpeechRecognitionLike = {
  continuous: boolean
  interimResults: boolean
  lang: string
  abort: () => void
  onend: (() => void) | null
  onerror: ((event: Event) => void) | null
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  start: () => void
  stop: () => void
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

type SpeechInputWindow = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor
  webkitSpeechRecognition?: SpeechRecognitionConstructor
}

export interface SpeechInputProps extends ComponentProps<typeof Button> {
  isListening?: boolean
  isProcessing?: boolean
  lang?: string
  onAudioRecorded?: (audioBlob: Blob) => Promise<string>
  onStart?: () => void | Promise<void>
  onStop?: () => void
  onTranscriptionChange?: (text: string) => void
  /**
   * Live interim (non-final) results from the Web Speech path, so non-LiveKit
   * environments still get a self-correcting preview. Called with the current
   * interim text ('' when there is no pending interim).
   */
  onInterimTranscript?: (text: string) => void
}

// --------------

function getSpeechRecognitionConstructor() {
  if (typeof window === 'undefined') {
    return undefined
  }

  const speechWindow = window as SpeechInputWindow
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition
}

// --------------

export function SpeechInput({
  children,
  className,
  isListening,
  isProcessing,
  lang = 'en-US',
  onAudioRecorded,
  onClick,
  onStart,
  onStop,
  onTranscriptionChange,
  onInterimTranscript,
  type = 'button',
  variant = 'ghost',
  ...props
}: SpeechInputProps) {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const [mode, setMode] = useState<SpeechInputMode>('unsupported')
  const [internalListening, setInternalListening] = useState(false)
  const [internalProcessing, setInternalProcessing] = useState(false)
  const isControlled = Boolean(onStart || onStop)
  const activeListening = isListening ?? internalListening
  const activeProcessing = isProcessing ?? internalProcessing

  useEffect(() => {
    if (isControlled) {
      return
    }

    if (getSpeechRecognitionConstructor()) {
      setMode('speech-recognition')
      return
    }

    if (
      typeof MediaRecorder !== 'undefined' &&
      Boolean(navigator.mediaDevices?.getUserMedia) &&
      onAudioRecorded
    ) {
      setMode('media-recorder')
      return
    }

    setMode('unsupported')
  }, [isControlled, onAudioRecorded])

  const cleanupMediaRecorder = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    recorderRef.current = null
    chunksRef.current = []
  }, [])

  const stopInternal = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      return
    }

    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop()
    }
  }, [])

  const startSpeechRecognition = useCallback(() => {
    const Recognition = getSpeechRecognitionConstructor()

    if (!Recognition) {
      return false
    }

    const recognition = new Recognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = lang
    recognition.onresult = (event) => {
      let finalTranscript = ''
      let interimTranscript = ''

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index]

        if (result?.isFinal) {
          finalTranscript += result[0]?.transcript ?? ''
        } else {
          interimTranscript += result[0]?.transcript ?? ''
        }
      }

      const transcript = finalTranscript.trim()

      if (transcript) {
        onTranscriptionChange?.(transcript)
      }

      // Interim preview (self-correcting). Fires even when there is no final
      // yet so non-LiveKit environments still show a live preview; cleared
      // ('') once the interim resolves into a final.
      onInterimTranscript?.(interimTranscript.trim())
    }
    recognition.onerror = () => {
      setInternalListening(false)
      onInterimTranscript?.('')
      recognitionRef.current = null
    }
    recognition.onend = () => {
      setInternalListening(false)
      onInterimTranscript?.('')
      recognitionRef.current = null
    }
    recognitionRef.current = recognition
    recognition.start()
    setInternalListening(true)

    return true
  }, [lang, onTranscriptionChange, onInterimTranscript])

  const startMediaRecorder = useCallback(async () => {
    if (!onAudioRecorded || !navigator.mediaDevices?.getUserMedia) {
      return false
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const recorder = new MediaRecorder(stream)
    chunksRef.current = []
    streamRef.current = stream
    recorderRef.current = recorder

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data)
      }
    }
    recorder.onstop = () => {
      const audioBlob = new Blob(chunksRef.current, {
        type: recorder.mimeType || 'audio/webm'
      })

      cleanupMediaRecorder()
      setInternalListening(false)
      setInternalProcessing(true)
      void onAudioRecorded(audioBlob)
        .then((transcript) => {
          const cleanTranscript = transcript.trim()

          if (cleanTranscript) {
            onTranscriptionChange?.(cleanTranscript)
          }
        })
        .finally(() => setInternalProcessing(false))
    }

    recorder.start()
    setInternalListening(true)

    return true
  }, [cleanupMediaRecorder, onAudioRecorded, onTranscriptionChange])

  const startInternal = useCallback(async () => {
    if (mode === 'speech-recognition') {
      startSpeechRecognition()
      return
    }

    if (mode === 'media-recorder') {
      await startMediaRecorder()
    }
  }, [mode, startMediaRecorder, startSpeechRecognition])

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort()
      cleanupMediaRecorder()
    }
  }, [cleanupMediaRecorder])

  const disabled = useMemo(() => {
    if (props.disabled) {
      return true
    }

    return !isControlled && mode === 'unsupported'
  }, [isControlled, mode, props.disabled])

  return (
    <Button
      {...props}
      aria-pressed={activeListening}
      className={cn(
        activeListening && 'text-primary',
        activeProcessing && 'cursor-default',
        className
      )}
      data-processing={activeProcessing ? true : undefined}
      disabled={disabled}
      type={type}
      variant={variant}
      onClick={(event) => {
        onClick?.(event)

        if (event.defaultPrevented) {
          return
        }

        if (isControlled) {
          if (activeListening) {
            onStop?.()
          } else {
            void onStart?.()
          }
          return
        }

        if (activeListening) {
          stopInternal()
        } else {
          void startInternal()
        }
      }}
    >
      {children ?? (
        <>
          {activeProcessing ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          ) : (
            <Mic
              aria-hidden="true"
              className={cn('size-4', activeListening && 'animate-pulse')}
            />
          )}
        </>
      )}
    </Button>
  )
}
